import { useCallback, useEffect, useMemo, useState } from "react";
import { coalesced } from "./chat/activity-refresh.ts";
import { useLive } from "./chat/run/live.ts";
import { useSeen } from "./chat/seen.ts";
import { read } from "./net/rally.ts";
import { badgeCountOf, showBadge } from "./push/badge.ts";
import { useUpdateReady } from "./update.ts";

type NewRow = {
  kind: string;
  id: string;
  conversation: string;
  title: string | null;
  at: string | null;
  unread?: number;
  muted?: boolean;
};

function loud(rows: NewRow[]): NewRow[] {
  return rows.filter((row) => (row.unread ?? 0) > 0 && !row.muted);
}

export function useActivityIndicator() {
  const [rows, setRows] = useState<NewRow[]>([]);

  const updated = useUpdateReady();

  const again = useCallback(() => {
    read<NewRow[]>("activity")
      .then(setRows)
      .catch(() => undefined);
  }, []);
  useEffect(again, [again]);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.conversation) refresh.notice();
  });
  useSeen(refresh.notice);

  const waiting = loud(rows);
  const many = waiting.reduce((all, row) => all + (row.unread ?? 0), 0);

  useEffect(() => showBadge(badgeCountOf(rows)), [rows]);

  return { many, updated };
}
